import React, { useEffect, useState } from "react";
import { TspFormField } from "../../../../components/primitives";
import TspLoadError from "../../../../components/common/TspLoadError";
import { DISTRIBUTION_METHODS, DISTRIBUTION_METHOD_LABEL } from "../../../../constants/tspOperations";
import { CUSTOM_BOOK_VALUE, LANGUAGE_OPTIONS } from "../../../book-distribution/components/BookDistributionRecordsCard";
import SectionShell, { FormButtons, SectionForm, autoVisitNote } from "./SectionShell";

// Book Distribution: the books given out at this place, one entry at a time
// (book, language, quantity, method). Entries belong to the place's recorded
// visit; without one, the first save records it (see autoVisitNote). store.books = [{ id, bookType, book_set_id, custom_book_name, language,
// qty, method }]; addBook / updateBook / removeBook take that shape.
export default function BookDistributionSection({ idPrefix, place, store, bookCatalog, canManage, disabled }) {
  const total = store.books.reduce((sum, row) => sum + Number(row.qty || 0), 0);
  return (
    <>
      <SectionShell
        idPrefix={idPrefix}
        title="Book Distribution"
        addLabel="Add Book Distribution"
        emptyText="No books recorded for this place yet."
        items={store.books}
        loading={store.booksLoading}
        error={store.booksError}
        onRetry={store.reloadBooks}
        canAdd={canManage}
        canEdit={() => canManage}
        canRemove={() => canManage}
        onAdd={store.addBook}
        onUpdate={store.updateBook}
        onRemove={store.removeBook}
        confirmRemove="Remove this book distribution entry?"
        removeLabel={(row) => `Remove ${row.qty} × ${row.bookType}`}
        disabled={disabled}
        renderItem={(row) => (
          <span className="tsp-pc-line">
            <strong>{row.bookType}</strong>
            <span className="tsp-muted-text"> · {row.language} · {DISTRIBUTION_METHOD_LABEL[row.method] || row.method}</span>
            <span className="tsp-pc-qty">{row.qty} books</span>
          </span>
        )}
        afterRecords={
          store.books.length > 0 ? (
            <p className="tsp-pc-total" aria-live="polite">
              Total at {place.city}: <strong>{total} books</strong>
            </p>
          ) : null
        }
        renderForm={(form) => <BookEntryForm {...form} bookCatalog={bookCatalog} />}
      />
    </>
  );
}

const EMPTY = { bookChoice: "", customBookName: "", language: "", qty: "1", method: "" };

function toDraft(row) {
  if (!row) return EMPTY;
  return { bookChoice: row.book_set_id ? String(row.book_set_id) : CUSTOM_BOOK_VALUE, customBookName: row.custom_book_name || "", language: row.language || "", qty: String(row.qty), method: row.method || "" };
}

function BookEntryForm({ initial, submit, cancel, busy, idPrefix, bookCatalog }) {
  const [draft, setDraft] = useState(() => toDraft(initial));
  const [error, setError] = useState("");
  const id = (field) => `${idPrefix}-${field}`;
  const requestCatalog = bookCatalog.request;
  useEffect(() => {
    requestCatalog?.();
  }, [requestCatalog]);
  const set = (key) => (event) => {
    setDraft({ ...draft, [key]: event.target.value });
    setError("");
  };

  if (bookCatalog.error) return <TspLoadError error={bookCatalog.error} onRetry={bookCatalog.reload} what="the book catalog" />;
  const books = bookCatalog.data;
  // A language saved before the list changed is still offered.
  const languages = draft.language && !LANGUAGE_OPTIONS.includes(draft.language) ? [draft.language, ...LANGUAGE_OPTIONS] : LANGUAGE_OPTIONS;

  const onSubmit = () => {
    const custom = draft.bookChoice === CUSTOM_BOOK_VALUE;
    const qty = Number(draft.qty);
    // An entry being edited keeps its own book's name while the catalog is
    // still loading (its book did not change, so there is nothing to look up).
    const unchangedBook = initial && !custom && initial.book_set_id && String(initial.book_set_id) === draft.bookChoice ? initial.bookType : null;
    const bookType = custom ? draft.customBookName.trim() : (books || []).find((book) => String(book.id) === draft.bookChoice)?.book_name || unchangedBook;
    if (!bookType || !draft.language || !draft.method || !Number.isInteger(qty) || qty < 1) {
      setError("Choose a book, language and method, and enter a quantity of at least 1.");
      return;
    }
    submit({
      bookType,
      book_set_id: custom ? null : Number(draft.bookChoice),
      custom_book_name: custom ? bookType : null,
      language: draft.language,
      qty,
      method: draft.method,
    });
  };

  return (
    <SectionForm onSubmit={onSubmit}>
      <div className="tsp-aj-grid">
        <TspFormField id={id("book")} label="Book" required>
          <select id={id("book")} className="tsp-input" value={draft.bookChoice} onChange={set("bookChoice")} disabled={busy || !books}>
            <option value="">{books ? "Choose a book" : "Loading the book catalog…"}</option>
            {!books && initial && initial.book_set_id && <option value={String(initial.book_set_id)}>{initial.bookType}</option>}
            {(books || []).map((book) => (
              <option key={book.id} value={book.id}>
                {book.book_name}
              </option>
            ))}
            <option value={CUSTOM_BOOK_VALUE}>Other / Not Listed</option>
          </select>
        </TspFormField>
        {draft.bookChoice === CUSTOM_BOOK_VALUE && (
          <TspFormField id={id("custom")} label="Book Name" required>
            <input id={id("custom")} type="text" className="tsp-input" maxLength={150} value={draft.customBookName} onChange={set("customBookName")} disabled={busy} />
          </TspFormField>
        )}
        <TspFormField id={id("language")} label="Language" required>
          <select id={id("language")} className="tsp-input" value={draft.language} onChange={set("language")} disabled={busy}>
            <option value="">Choose a language</option>
            {languages.map((language) => (
              <option key={language} value={language}>
                {language}
              </option>
            ))}
          </select>
        </TspFormField>
        <TspFormField id={id("qty")} label="Quantity" required>
          <input id={id("qty")} type="number" min="1" step="1" className="tsp-input" value={draft.qty} onChange={set("qty")} disabled={busy} />
        </TspFormField>
        <TspFormField id={id("method")} label="Distribution Method" required>
          <select id={id("method")} className="tsp-input" value={draft.method} onChange={set("method")} disabled={busy}>
            <option value="">Choose a method</option>
            {DISTRIBUTION_METHODS.map((method) => (
              <option key={method.value} value={method.value}>
                {method.label}
              </option>
            ))}
          </select>
        </TspFormField>
      </div>
      {error && (
        <p className="tsp-form-error" role="alert">
          {error}
        </p>
      )}
      <FormButtons busy={busy} onSave={onSubmit} onCancel={cancel} saveLabel="Save Book Distribution" />
    </SectionForm>
  );
}
